import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import MetricCard from "../../../components/Cards/MetricCard";
import ExportToExcelButton from "../../../utils/ExportToExcel";
import { ErrorToast } from "../../../utils/ShowToast";
import { GetAllItemQtyAdjustmentLogsApi } from "../../../ApiRequests";
import { getAppBasePath } from "../../../utils/appPaths";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import { usePageRefresh } from "../../../hooks/usePageRefresh";
import { formatDateTime } from "../../../utils/dateTime";

const fmtQty = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    maximumFractionDigits: 2,
  });

const StockQtyAdjustmentReport = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const base = getAppBasePath();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetAllItemQtyAdjustmentLogsApi(
        itemId ? { itemId } : undefined
      );
      const payload = res?.data?.data?.payload;
      setRows(Array.isArray(payload?.rows) ? payload.rows : []);
      setSummary(payload?.summary || null);
    } catch (e) {
      setRows([]);
      setSummary(null);
      ErrorToast(
        e.response?.data?.error?.msg || "Could not load stock adjustment report"
      );
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  usePageRefresh(load, [itemId]);

  const searchColumns = useMemo(
    () => [
      { id: "item_name" },
      { id: "item_code" },
      { id: "reason" },
      { id: "performedByName" },
      { id: "branch_name" },
      { id: "delta" },
    ],
    []
  );

  const filteredRows = useMemo(
    () => filterTableRowsBySearch(rows, search, searchColumns),
    [rows, search, searchColumns]
  );

  const exportRows = useMemo(
    () =>
      filteredRows.map((row) => ({
        When: row.createdAt
          ? formatDateTime(row.createdAt)
          : "",
        Code: row.item_code || "",
        Item: row.item_name || "",
        Branch: row.branch_name || "",
        Delta: row.delta,
        "Qty before": row.previousQty,
        "Qty after": row.newQty,
        "In before": row.previousInQty ?? "",
        "In after": row.newInQty ?? "",
        Reason: row.reason || "",
        By: row.performedByName || "",
      })),
    [filteredRows]
  );

  const titleItem =
    itemId && rows[0]?.item_name
      ? ` — ${rows[0].item_name}`
      : itemId
      ? " — Item"
      : "";

  return (
    <BodyWrapper>
      <Header
        title={`Stock qty adjustment report${titleItem}`}
        desc="Every +/− stock change from Adjust stock quantity"
        Back
      >
        {!itemId ? null : (
          <button
            type="button"
            className="px-3 py-2 text-sm font-bold rounded-lg border border-main text-main hover:bg-main hover:text-white transition-colors"
            onClick={() => navigate(`${base}/stock-qty-log`)}
          >
            All items
          </button>
        )}
        <ExportToExcelButton
          data={exportRows}
          fileName="Stock-Qty-Adjustments"
        />
      </Header>

      {loading ? (
        <div className="flex justify-center py-16">
          <FetchingLoading />
        </div>
      ) : (
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <MetricCard
              title="Entries"
              value={fmtQty(summary?.total_entries)}
              subtitle="Logged adjustments"
              tone="neutral"
            />
            <MetricCard
              title="Increased"
              value={`+${fmtQty(summary?.increased_qty)}`}
              subtitle={`${summary?.increase_count || 0} increase entries`}
              tone="success"
            />
            <MetricCard
              title="Decreased"
              value={`−${fmtQty(summary?.decreased_qty)}`}
              subtitle={`${summary?.decrease_count || 0} decrease entries`}
              tone="danger"
            />
            <MetricCard
              title="Net change"
              value={fmtQty(summary?.net_qty)}
              subtitle="Increased − decreased"
              tone="brand"
            />
          </div>

          <div className="mb-4">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item, code, reason, user…"
              className="w-full max-w-md h-10 px-3 text-sm font-semibold border border-line rounded-lg outline-none bg-card text-ink placeholder:text-muted focus:border-main focus:ring-2 focus:ring-main/15"
            />
          </div>

          {filteredRows.length === 0 ? (
            <p className="text-muted text-sm py-8 text-center">
              No stock quantity adjustments recorded yet.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-line shadow-soft">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-main text-white">
                    <th className="border border-line px-2 py-2 text-left">
                      When
                    </th>
                    <th className="border border-line px-2 py-2 text-left">
                      Item
                    </th>
                    <th className="border border-line px-2 py-2 text-left">
                      Branch
                    </th>
                    <th className="border border-line px-2 py-2">Δ</th>
                    <th className="border border-line px-2 py-2">Before</th>
                    <th className="border border-line px-2 py-2">After</th>
                    <th className="border border-line px-2 py-2 text-left">
                      Reason
                    </th>
                    <th className="border border-line px-2 py-2 text-left">By</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row._id} className="font-quicksand bg-card">
                      <td className="border border-line px-2 py-1.5 whitespace-nowrap">
                        {row.createdAt
                          ? formatDateTime(row.createdAt)
                          : "—"}
                      </td>
                      <td className="border border-line px-2 py-1.5">
                        <div className="font-semibold text-ink">
                          {row.item_name || "—"}
                        </div>
                        <div className="text-xs text-muted">
                          {row.item_code || ""}
                        </div>
                      </td>
                      <td className="border border-line px-2 py-1.5">
                        {row.branch_name || "—"}
                      </td>
                      <td
                        className={`border border-line px-2 py-1.5 text-center font-bold ${
                          row.delta > 0 ? "text-success" : "text-danger"
                        }`}
                      >
                        {row.delta > 0 ? `+${row.delta}` : row.delta}
                      </td>
                      <td className="border border-line px-2 py-1.5 text-center tabular-nums">
                        {row.previousQty}
                      </td>
                      <td className="border border-line px-2 py-1.5 text-center tabular-nums">
                        {row.newQty}
                      </td>
                      <td className="border border-line px-2 py-1.5 max-w-[240px]">
                        {row.reason}
                      </td>
                      <td className="border border-line px-2 py-1.5 text-xs">
                        {row.performedByName || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </BodyWrapper>
  );
};

export default StockQtyAdjustmentReport;
